import { useMutation } from "@tanstack/react-query";
import { useGetList, useRefresh } from "react-admin";
import { httpClient, getPartnerAwareApiUrl } from "@/providers/dataProvider/dataProvider";
import type { AccommodationRun } from "@/types";
import type { AccommodationEnrollmentInput } from "@/types/policyMonthlyEnrollment";

export const useAccommodationRuns = (policyId: string | undefined) => {
  const refresh = useRefresh();

  const { data, isLoading, isError } = useGetList<AccommodationRun>(
    `policies/${policyId}/accommodation-runs`,
    {
      pagination: { page: 1, perPage: 1000 },
      sort: { field: "run_date", order: "DESC" },
    },
    { enabled: !!policyId }
  );

  // Story #4629: triggerCalculation accepts an optional `runMonth` (ISO date string
  // "YYYY-MM-01"). When omitted, the backend defaults to the most recent completed
  // calendar month (current month − 1).
  // `enrollments` entered in the run dialog are sent with the run and saved
  // by the backend in the run's transaction.
  const { mutate: triggerCalculation, isPending: isTriggering } = useMutation({
    mutationFn: async (vars: { runMonth?: string; enrollments?: AccommodationEnrollmentInput[] } | undefined) => {
      const payload: { run_month?: string; enrollments?: AccommodationEnrollmentInput[] } = {};
      if (vars?.runMonth) payload.run_month = vars.runMonth;
      if (vars?.enrollments?.length) payload.enrollments = vars.enrollments;
      const body = Object.keys(payload).length > 0 ? JSON.stringify(payload) : undefined;
      const { json } = await httpClient(
        `${getPartnerAwareApiUrl()}/policies/${policyId}/accommodation-runs/calculate`,
        {
          method: "POST",
          body,
        }
      );
      return json as AccommodationRun;
    },
    onSuccess: () => refresh(),
  });

  // `enrollments` behave as on triggerCalculation.
  const { mutate: reconcile, isPending: isReconciling } = useMutation({
    mutationFn: async (vars: { enrollments?: AccommodationEnrollmentInput[] } | undefined) => {
      const body = vars?.enrollments?.length ? JSON.stringify({ enrollments: vars.enrollments }) : undefined;
      const { json } = await httpClient(
        `${getPartnerAwareApiUrl()}/policies/${policyId}/accommodation-runs/reconcile`,
        {
          method: "POST",
          body,
        }
      );
      return json as AccommodationRun;
    },
    onSuccess: () => refresh(),
  });

  return {
    runs: data ?? [],
    isLoading,
    isError,
    triggerCalculation,
    isTriggering,
    reconcile,
    isReconciling,
  };
};
